import type { UserDocument } from "@/api/models"

import { formatBytes, getReadableFileType } from "./file-picker/utils"

export type ChatSelectedFileItemKind = "document" | "session" | "indexed" | "file"

export type ChatSelectedFileItem = {
  key: string
  kind: ChatSelectedFileItemKind
  label: string
  detail: string
  searchableText?: string
  previewCitation: string | null
  document?: UserDocument
  file?: File
  removeValue: File | string
}

export type BuildChatSelectedFileItemsOptions = {
  availableFiles?: File[]
  selectedDocuments?: UserDocument[]
  filesInIndex?: string[]
  sessionFileRefs?: string[]
  projectId?: string
  labels?: ChatSelectedFileItemLabels
}

export type ChatSelectedFileSummary = {
  visibleItems: ChatSelectedFileItem[]
  overflowCount: number
}

export type ChatSelectedFileItemLabels = {
  document: string
  file: string
  fileTypeSuffix: string
  indexedFile: string
  sessionFile: string
}

export type ChatSelectedFileItemTranslate = (
  key: string,
  options?: { defaultValue?: string }
) => string

const DEFAULT_SELECTED_FILE_LABELS: ChatSelectedFileItemLabels = {
  document: "Document",
  file: "File",
  fileTypeSuffix: "file",
  indexedFile: "Indexed file",
  sessionFile: "Session file",
}

export function getChatSelectedFileItemLabels(
  t: ChatSelectedFileItemTranslate
): ChatSelectedFileItemLabels {
  return {
    document: t("questioninput.selected_file_detail_document", { defaultValue: "Document" }),
    file: t("questioninput.selected_file_detail_file", { defaultValue: "File" }),
    fileTypeSuffix: t("questioninput.selected_file_detail_file_suffix", {
      defaultValue: "file",
    }),
    indexedFile: t("questioninput.selected_file_detail_indexed", {
      defaultValue: "Indexed file",
    }),
    sessionFile: t("questioninput.selected_file_detail_session", {
      defaultValue: "Session file",
    }),
  }
}

function getLocalFileType(file: File, labels: ChatSelectedFileItemLabels): string {
  const hasExtension = file.name.includes(".") && Boolean(file.name.split(".").pop())
  if (hasExtension) return getReadableFileType(file.name, file.type) || labels.file

  if (file.type) {
    const main = file.type.split("/")[0]
    const readableMain = `${main.charAt(0).toUpperCase()}${main.slice(1)}`
    return `${readableMain} ${labels.fileTypeSuffix}`
  }

  return labels.file
}

function getLocalFileDetail(file: File, labels: ChatSelectedFileItemLabels): string {
  const type = getLocalFileType(file, labels)
  const size = formatBytes(file.size)
  return size ? `${type} · ${size}` : type
}

export function buildChatSelectedFileItems({
  availableFiles,
  selectedDocuments,
  filesInIndex,
  sessionFileRefs,
  projectId,
  labels = DEFAULT_SELECTED_FILE_LABELS,
}: BuildChatSelectedFileItemsOptions): ChatSelectedFileItem[] {
  const localFiles = availableFiles ?? []
  const localFileNames = new Set(localFiles.map((file) => file.name))
  const sessionNames = sessionFileRefs ?? []
  const sessionFileNames = new Set(sessionNames)

  const documentItems: ChatSelectedFileItem[] = (selectedDocuments ?? []).map((document) => {
    const label = document.title || "Untitled document"
    return {
      key: `document-${document.id}`,
      kind: "document",
      label,
      detail: labels.document,
      searchableText: document.content,
      previewCitation: `doc:${document.id}`,
      document,
      removeValue: document.id,
    }
  })

  const sessionItems: ChatSelectedFileItem[] = sessionNames
    .filter((name) => !localFileNames.has(name))
    .map((name) => ({
      key: `session-${name}`,
      kind: "session",
      label: name,
      detail: labels.sessionFile,
      previewCitation: name,
      removeValue: name,
    }))

  const indexedItems: ChatSelectedFileItem[] = projectId
    ? []
    : (filesInIndex ?? [])
        .filter((name) => !sessionFileNames.has(name))
        .map((name) => ({
          key: `indexed-${name}`,
          kind: "indexed",
          label: name,
          detail: labels.indexedFile,
          previewCitation: name,
          removeValue: name,
        }))

  const localItems: ChatSelectedFileItem[] = localFiles.map((file) => ({
    key: `file-${file.name}-${file.lastModified}`,
    kind: "file",
    label: file.name,
    detail: getLocalFileDetail(file, labels),
    previewCitation: null,
    file,
    removeValue: file,
  }))

  return [...documentItems, ...sessionItems, ...indexedItems, ...localItems]
}

export function getChatSelectedFileSummary(
  items: ChatSelectedFileItem[],
  visibleCount = 2
): ChatSelectedFileSummary {
  const safeVisibleCount = Math.max(0, visibleCount)
  return {
    visibleItems: items.slice(0, safeVisibleCount),
    overflowCount: Math.max(0, items.length - safeVisibleCount),
  }
}
